/* @flow */

import type { InfoBlock } from "shop-state/types";

import React from "react";
import cn from "classnames";
import { Link } from "react-router-dom";
import { useTranslate } from "@awardit/react-use-translate";

import Carousel from "components/Carousel";
import { Item } from "components/UiComponents";
import useBrowserDimensions from "helpers/use-browser-dimensions";

import styles from "./styles.scss";

type Props = {
  items: $ReadOnlyArray<InfoBlock>,
  route: ?string,
};

type CardProps = {
  info: InfoBlock,
  route: ?string,
};

const small = Number.parseInt(styles.small, 10);

const CurrentPageInfoCard = ({
  info,
  route,
}: CardProps): React$Node => {
  const t = useTranslate();
  const image = info.gridImage || info.image;
  const description = info.description || info.content;

  return (
    <div
      className={cn(
        styles.currentPageInfoCard,
        "awardit-currentPageInfoCard",
        `awardit-currentPageInfoCard-${info.id}`
      )}
    >
      {image &&
        <div className={styles.currentPageInfoCardImage}>
          <img src={image} alt="" />
        </div>
      }
      <div className={styles.currentPageInfoCardContent}>
        {info.title && <h3>{info.title}</h3>}
        {/* eslint-disable react/no-danger */}
        {description &&
          <div
            dangerouslySetInnerHTML={{ __html: description }}
            className={cn(
              styles.currentPageInfoCardDescription,
              "awardit-currentPageInfoCardDescription"
            )}
          />
        }
        {/* eslint-enable react/no-danger */}
        {info.content && info.content.length > 0 && route &&
          <Link className={styles.readMore} to={`${route}/${info.id}`}>
            {t("COMMON.READ_MORE")}
          </Link>
        }
      </div>
    </div>
  );
};

const CurrentPageInfoSection = ({
  items,
  route,
}: Props): React$Node => {
  const { width: browserWidth } = useBrowserDimensions();
  const isSmallViewport = browserWidth > 0 && browserWidth <= small;
  const visibleItems = items.slice(0, 5);

  if (visibleItems.length === 0) {
    return null;
  }

  return (
    isSmallViewport ?
      <Carousel
        adaptiveHeight
        className={styles.carousel}
        items={visibleItems.map(info => (
          <Item key={info.id} className={styles.carouselItem}>
            <CurrentPageInfoCard info={info} route={route} />
          </Item>
        ))}
        slidesToShow={2}
        timer={7500}
      /> :
      <div className={cn(styles.currentPageInfoSectionGrid, "awardit-currentPageInfoSectionGrid")}>
        <div className={styles.currentPageInfoCardsWrapper}>
          <CurrentPageInfoCard info={visibleItems[0]} route={route} />
          <div className={cn(styles.column, styles.columnLeft)}>
            {visibleItems.slice(1, 3).map(info => (
              <CurrentPageInfoCard key={info.id} info={info} route={route} />
            ))}
          </div>
        </div>
        <div className={styles.currentPageInfoCardsWrapper}>
          <div className={cn(styles.column, styles.columnRight)}>
            {visibleItems.slice(3, 5).map(info => (
              <CurrentPageInfoCard
                key={info.id}
                info={info}
                route={route}
              />
            ))}
          </div>
        </div>
      </div>
  );
};

export default CurrentPageInfoSection;
